import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../../../fb";
import {
  doc,
  setDoc,
  getDoc,
  collection,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { taskStatus } from "@/constants";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { filterShopifyTask } from "@/store/utils/shopifyTasksUtils";
import axios from "axios";
import {
  convertAndUploadPngUrlToFirebase,
  currentMonthYear,
  getImageUrl,
} from "@/utils";
import { EventBus, events } from "../../../event-bus";

/** Refinement-Verlauf fortschreiben: bestehende Versionen + Legacy-Einzelwert
 *  (alte Docs kennen nur refinementArtwork) + neue URL. Grundlage für die
 *  "Refinement 1..N"-Einträge in der Versions-Leiste des LiveEditorDialog. */
const appendRefinementVersion = (customizationData, url, at) => {
  const list = [...((customizationData && customizationData.refinementVersions) || [])];
  const legacy = customizationData && customizationData.refinementArtwork;
  if (legacy && !list.some((v) => v.url === legacy)) {
    list.push({ url: legacy, at: (customizationData && customizationData.refinementArtworkAt) || 0 });
  }
  if (url && !list.some((v) => v.url === url)) {
    list.push({ url, at });
  }
  return list;
};


const defaultState = {
  allTasks: {
    OH: [],
    AQ: [],
    TM: [],
    live: [],
  },
  refinementTasks: [],
  searchTerm: "",
  filter: "unmarked",
  shopifyTasksLoading: true,
  shopifyTasksErrorMessage: "",
  taskIdsAlreadyImported: [],
  initial: true,
  markedTaskIds: [],
  selectedAssignee: "",
  selectedShopId: "TM",
  unsubShopifyTasks: () => {},
};

const getters = {
  shopifyTasksTM: (state) =>
    state.allTasks.TM.filter((task) => {
      return filterShopifyTask({
        task: task,
        term: state.searchTerm,
        filter: state.filter,
      });
    }),
  shopifyTasksAQ: (state) =>
    state.allTasks.AQ.filter((task) => {
      return filterShopifyTask({
        task: task,
        term: state.searchTerm,
        filter: state.filter,
      });
    }),
  shopifyTasksOH: (state) =>
    state.allTasks.OH.filter((task) => {
      return filterShopifyTask({
        task: task,
        term: state.searchTerm,
        filter: state.filter,
      });
    }),
  shopifyTasksLive: (state) =>
    state.allTasks.live.filter((task) => {
      return filterShopifyTask({
        task: task,
        term: state.searchTerm,
        filter: state.filter,
      });
    }),
  shopifyTasksLength: (state) =>
    state.allTasks.AQ.length +
    state.allTasks.TM.length +
    state.allTasks.OH.length +
    state.allTasks.live.length,
  selectedShopId: (state) => state.selectedShopId,
  shopifyTasksLoading: (state) => state.shopifyTasksLoading,
  shopifyTaskFilter: (state) => state.filter,
  selectedAssignee: (state) => state.selectedAssignee,
  unmarkedTaskIdsLength: (state) => {
    let i = 0;
    Object.keys(state.allTasks).forEach((shopId) => {
      i += state.allTasks[shopId].filter((task) => !task.isMarked)?.length || 0;
    });
    return i;
  },
  /** Nicht geflaggte Tasks, für die der Auto-Import eingeplant ist (task.autoImport schreibt der Server) */
  autoImportTaskIdsLength: (state) => {
    let i = 0;
    Object.keys(state.allTasks).forEach((shopId) => {
      i += state.allTasks[shopId].filter((task) => !task.isMarked && task.autoImport && task.autoImport.state === "scheduled")?.length || 0;
    });
    return i;
  },
  markedTaskIdsLength: (state) => {
    let i = 0;
    Object.keys(state.allTasks).forEach((shopId) => {
      i += state.allTasks[shopId].filter((task) => task.isMarked)?.length || 0;
    });
    return i;
  },
};

const actions = {
  async unsubscribeShopifyTasks({ state }) {
    if (typeof state.unsubShopifyTasks === "function") {
      state.unsubShopifyTasks();
    }
    state.unsubShopifyTasks = () => {};
  },
  async loadTasksFromShopify({ commit, state }) {
    // Idempotent: if a live listener is already attached, keep it.
    if (state.unsubShopifyTasks && state.unsubShopifyTasks.__active) {
      return;
    }

    commit("setShopifyTasksLoading", true);
    commit("setAllTasks", { shopId: "AQ", tasks: [] });
    commit("setAllTasks", { shopId: "TM", tasks: [] });
    commit("setAllTasks", { shopId: "OH", tasks: [] });
    commit("setAllTasks", { shopId: "live", tasks: [] });
    const shopifyTasksCollection = collection(db, "shopifyTasks");
    // const q = query(shopifyTasksCollection, orderBy("createdAt"));
    const q = query(shopifyTasksCollection, orderBy("createdAt"), limit(10000));

    if (typeof state.unsubShopifyTasks === "function") {
      state.unsubShopifyTasks();
    }
    const unsub = onSnapshot(q, (snapshot) => {
      if (!snapshot.docs.length && state.initial) {
        // No documents, initial load is complete, but no data.
        commit("setShopifyTasksLoading", false);
      }
      snapshot.docChanges().forEach((change) => {
        const taskData = {
          ...change.doc.data(),
        };
        if (change.type === "added") {
          commit("addShopifyTask", {
            task: taskData,
            shopId: taskData.shopId,
            liveEditor: taskData?.customizationData?.id || false,
          });
        } else if (change.type === "modified") {
          commit("updateShopifyTask", {
            task: taskData,
            shopId: taskData.shopId,
            liveEditor: taskData?.customizationData?.id || false,
          });
        } else if (change.type === "removed") {
          commit("updateShopifyTask", {
            task: {
              ...taskData,
              removed: true,
            },
            shopId: taskData.shopId,
            liveEditor: taskData?.customizationData?.id || false,
          });
        }

        if (state.initial) {
          // This was the first snapshot, which means initial loading is done.
          commit("setShopifyTasksLoading", false);
          // After the initial load, set isInitialLoad to false so that subsequent changes do not trigger this block
        }
      });
    });
    unsub.__active = true;
    state.unsubShopifyTasks = unsub;
  },
  // eslint-disable-next-line no-empty-pattern
  async markTask({}, { uid, autoReopen, revisionComment }) {
    const taskToMarkDoc = doc(db, "shopifyTasks", uid);
    getDoc(taskToMarkDoc).then(async (task) => {
      const taskData = task.data();
      await setDoc(
        taskToMarkDoc,
        {
          revisionComment: revisionComment || null,
          isMarked: !taskData.isMarked,
          feedback: null,
          timestampMarked: !taskData.isMarked
            ? autoReopen
              ? Date.now()
              : null
            : null,
        },
        { merge: true }
      );

      const updateOrderTags = httpsCallable(
        functions,
        "updateShopifyOrderTags"
      );
      await updateOrderTags({
        orderId: taskData.orderId,
        tags: "ON HOLD",
        countryCode: taskData.countryCode,
        shopId: taskData.shopId,
      });
    });
  },
  // eslint-disable-next-line no-empty-pattern
  async editTask({}, { task }) {
    const taskToEditDoc = doc(db, "shopifyTasks", task.uid);
    await setDoc(taskToEditDoc, task);

    EventBus.$emit(events.SNACKBAR, {
      message: `Updated Task ${task.orderName}`,
      type: "success",
    });
  },
  // eslint-disable-next-line no-empty-pattern
  async cancelTask({}, { task }) {
    try {
      setDoc(doc(db, `tasks`, task?.uid.toString()), {
        taskKey: task.taskKey || "",
        uid: task.uid,
        orderId: task.orderId,
        referrer: task.referrer || null,
        lineItemId: task.lineItemId,
        orderName: task.orderName,
        orderNote: task.orderNote,
        productType: task.productType,
        printVariant: task.printVariantId,
        printVariantName: task.printVariantName,
        thumb: task.imgThumb,
        image: task.img,
        img2: task.img2 || "",
        imgThumb2: task.imgThumb2 || "",
        result: "",
        status: taskStatus.CANCELED,
        templateThumb: task.templateThumb || "",
        template: task.template || "",
        editors: {
          IMPORTED: auth.currentUser.uid,
          CLOSED: auth.currentUser.uid,
        },
        createdAt: task.createdAt,
        shopId: task.shopId,
        countryCode: task.countryCode,
      }).then(async () => {
        const orderDoc = doc(db, `orders`, task?.orderId);
        await getDoc(orderDoc).then((orderRes) => {
          const order = orderRes.data();
          setDoc(
            orderDoc,
            {
              tasks: order.tasks.map((orderTask) => {
                if (orderTask.taskUid === task.uid) {
                  return {
                    ...orderTask,
                    cancelled: true,
                  };
                } else {
                  return orderTask;
                }
              }),
            },
            { merge: true }
          ).then(async () => {
            const taskToCancel = doc(db, "shopifyTasks", task.uid);
            await deleteDoc(taskToCancel);
          });
        });
      });
    } catch (e) {
      EventBus.$emit(events.SNACKBAR, {
        message: `Error cancel Task: ${e.message}`,
        type: "error",
      });
    }
  },
  async importTaskFromShopify(
    { state },
    {
      task,
      callBack,
      image,
      image2,
      customImage,
      customTemplate,
      usedUpscaling,
      existentTask,
      selectedAssignee,
      originalImage,
      originalImageThumb,
    }
  ) {
    let customUrl1 = null;
    let customUrl2 = null;
    let customUrl1Thumb = null;
    let customUrl2Thumb = null;
    let customImageFile1 = customImage?.[1] || null;
    let customImageFile2 = customImage?.[2] || null;
    let template = task.template;
    let templateThumb = task.templateThumb;

    // If the image was upscaled wee need to download it and upload it to own server
    if (usedUpscaling?.[1] && !customImage?.[1]) {
      const imageDownloadResponse = await axios.get(image, {
        responseType: "blob",
      });
      customImageFile1 = await imageDownloadResponse.data;
    }

    if (usedUpscaling?.[2] && !customImage?.[2]) {
      const imageDownloadResponse = await axios.get(image2, {
        responseType: "blob",
      });
      customImageFile2 = await imageDownloadResponse.data;
    }

    if (customImageFile1) {
      const storage = getStorage();
      const storageRef = ref(
        storage,
        `/customTaskImages/${currentMonthYear}/${task.uid}_1.png`
      );
      const uploadResponse = await uploadBytes(storageRef, customImageFile1);
      customUrl1 = await getDownloadURL(uploadResponse.ref);
      customUrl1Thumb = getImageUrl(
        `customTaskImages/${currentMonthYear}/thumbnails/${task.uid}_1_500x500.png`
      );
    }

    if (customImageFile2) {
      const storage = getStorage();
      const storageRef = ref(
        storage,
        `/customTaskImages/${currentMonthYear}/${task.uid}_2.png`
      );
      const uploadResponse = await uploadBytes(storageRef, customImageFile2);
      customUrl2 = await getDownloadURL(uploadResponse.ref);
      customUrl2Thumb = getImageUrl(
        `customTaskImages/${currentMonthYear}/thumbnails/${task.uid}_2_500x500.png`
      );
    }

    if (customTemplate) {
      const storage = getStorage();
      const storageRef = ref(
        storage,
        `/customTaskImages/${currentMonthYear}/${task.uid}_temp.png`
      );
      const uploadResponse = await uploadBytes(storageRef, customTemplate);
      template = await getDownloadURL(uploadResponse.ref);
      templateThumb = getImageUrl(
        `customTaskImages/${currentMonthYear}/thumbnails/${task.uid}_2_500x500.png`
      );
    }

    let finalTemplate = null;
    let finalTemplateThumbnailUrl = null;
    let result = null;
    let resultThumbnailUrl = null;
    let status = taskStatus.DRAW;

    // If does not result in task, we need to set the required fields for finalTemplate and Result
    if (
      !task.productType.resultsInTask &&
      !task.productType.name.includes("Live")
    ) {
      const getTemplateFromTeeinblue = httpsCallable(
        functions,
        "getTemplateFromTeeinblue"
      );

      const templateRes = await getTemplateFromTeeinblue({
        orderId: task.orderId,
        customizationId: task.teeInBlue.customizationId,
        countryCode: task.countryCode,
        shopId: task.shopId,
      });
      result = templateRes.data;

      try {
        const jpgTemp = await convertAndUploadPngUrlToFirebase(
          templateRes.data
        );
        finalTemplate = jpgTemp;
      } catch (e) {
        throw e;
      }

      finalTemplateThumbnailUrl = task.img;
      resultThumbnailUrl = task.img;
      status = taskStatus.CLOSED;
    }

    if (existentTask) {
      if (task.productType.name.includes("Live")) {
        console.log(existentTask);
        result = existentTask.result;
        resultThumbnailUrl = existentTask.resultThumbnailUrl || "";
        finalTemplate = existentTask.finalTemplate || "";
        finalTemplateThumbnailUrl =
          existentTask.finalTemplateThumbnailUrl || "";

        const mailResults = httpsCallable(functions, "mailResults");
        await mailResults({
          taskUid: task.uid,
          template: finalTemplate,
          orientation:
            existentTask.customizationData?.configuration?.orientation || null,
          isLivePreview: true,
        });

        status = taskStatus.CLOSED;
      } else {
        result = existentTask.result;
        resultThumbnailUrl = existentTask.resultThumbnailUrl || "";
        status = taskStatus.TEMPLATING;
      }
    }

    // Creates A DB Entry for task (Excluding personal customer Information)
    await setDoc(doc(db, `tasks`, task?.uid.toString()), {
      taskKey: task.taskKey || "",
      orderKey: task.orderKey || "",
      uid: task.uid,
      orderId: task.orderId,
      referrer: task.referrer || null,
      lineItemId: task.lineItemId,
      orderName: task.orderName,
      orderNote: task.orderNote,
      productType: {
        productName: task.productName,
        ...task.productType,
      },
      printVariant: task.printVariantId,
      printVariantName: task.printVariantName,
      thumb: customUrl1Thumb || task.imgThumb || "",
      image: customUrl1 || image,
      image2: customUrl2 || image2 || "",
      image2Thumb: customUrl2Thumb || task.img2Thumb || "",
      result: result,
      status: status,
      templateThumb: templateThumb,
      template: template,
      finalTemplate: finalTemplate,
      editors: {
        IMPORTED: auth.currentUser.uid,
      },
      createdAt: task.createdAt,
      assignedTo: !!existentTask ? "" : selectedAssignee, // existentTask means it will not be directed template -> no assignee
      countryCode: task.countryCode,
      shopId: task.shopId,
      finalTemplateThumbnailUrl,
      resultThumbnailUrl,
      numberOfSubTasks: task.numberOfSubTasks,
      originalImage: originalImage || "",
      originalImageThumb: originalImage || "",
      extras: task.extras || [],
    });

    const taskToHide = doc(db, "shopifyTasks", task.uid);
    await deleteDoc(taskToHide);
    callBack();
  },
  async importLiveTaskRefinement(
    { state },
    {
      task,
      callBack,
      customOriginalImage,
      revisionComment,
      refinementUrl,
      currentConfig,
    }
  ) {
    let customFinal = null;
    let customFinalThumb = null;
    let customOriginal = null;
    let customOriginalThumb = null;

    const uploadFile = async (file, folderName) => {
      let img;
      let thumb;
      if (typeof file === "string") {
        console.log("customImage is a URL, skipping upload.");
        // Es ist bereits eine URL (vom LiveEditor), wir übernehmen sie direkt
        img = file;
        thumb = file; // Thumb ist in diesem Fall das gleiche
      } else {
        // Es ist ein File/Blob (vom Drag & Drop), wir müssen hochladen
        console.log("customImage is a File, uploading...");
        const shortId = Math.random().toString(36).substr(2, 6);
        const storage = getStorage();
        const storageRef = ref(
          storage,
          `/${folderName}/${currentMonthYear}/${task.uid}_${shortId}.jpg`
        );
        const uploadResponse = await uploadBytes(storageRef, file);
        img = await getDownloadURL(uploadResponse.ref);
        thumb = getImageUrl(
          `/${folderName}/${currentMonthYear}/${task.uid}_${shortId}.jpg`
        );
      }
      return {
        img,
        thumb,
      };
    };
    if (customOriginalImage) {
      const { img, thumb } = uploadFile(
        customOriginalImage,
        "customTaskImages"
      );
      customOriginal = img;
      customOriginalThumb = thumb;
    }

    const image =
      customOriginal || task.customizationData?.originalImage || null;
    const finalTemplate =
      refinementUrl || this.customizationData?.artworkImageUrl;

    await setDoc(doc(db, `tasks`, task?.uid.toString()), {
      taskKey: task.taskKey || "",
      orderKey: task.orderKey || "",
      uid: task.uid,
      orderId: task.orderId,
      referrer: task.referrer || null,
      lineItemId: task.lineItemId,
      orderName: task.orderName,
      orderNote: task.orderNote,
      revisionComment: {
        comment: revisionComment || "",
        userName: auth.currentUser.displayName,
        timestamp: new Date().toISOString(),
      },
      productType: {
        productName: task.productName,
        ...task.productType,
      },
      printVariant: task.printVariantId,
      printVariantName: task.printVariantName,
      thumb: customOriginalThumb || image || null,
      image: image || null,
      result: task.customizationData.previewImg || null,
      resultThumbnailUrl: task.customizationData.previewImg || null,
      status: taskStatus.REFINEMENT,
      template: null,
      finalTemplate: null,
      finalTemplateThumbnailUrl: null,
      editors: {
        IMPORTED: auth.currentUser.uid,
      },
      customizationData: {
        ...task.customizationData,
        refinementArtwork: refinementUrl || null,
        // frische Refinement-URL → Zeitstempel für Neueste-Version-Erkennung
        ...(refinementUrl ? { refinementArtworkAt: Date.now() } : {}),
        refinementVersions: appendRefinementVersion(
          task.customizationData,
          refinementUrl || null,
          Date.now()
        ),
        savedConfig: currentConfig || null,
      },
      createdAt: task.createdAt,
      assignedTo: "", // existentTask means it will not be directed template -> no assignee
      countryCode: task.countryCode,
      shopId: task.shopId,
      numberOfSubTasks: task.numberOfSubTasks,
      extras: task.extras || [],
    });

    const taskToHide = doc(db, "shopifyTasks", task.uid);
    await deleteDoc(taskToHide);
    callBack();
  },
  async sendLiveTaskToFulfill(
    { state },
    {
      task,
      callBack,
      customImage,
      finalArtworkUrl,
      sendPreviewCustomer,
      refinementUrl,
      currentConfig,
    }
  ) {},
  async importLiveTaskFromShopify(
    { state },
    {
      task,
      callBack,
      customImage,
      finalArtworkUrl,
      sendPreviewCustomer,
      refinementUrl,
      currentConfig,
    }
  ) {
    // If the image was upscaled wee need to download it and upload it to own server
    const originalImage = task?.customizationData?.originalImage;
    let finalTemplate = finalArtworkUrl || null;
    let finalTemplateThumb = finalArtworkUrl;
    const finalRefineUrl =
      refinementUrl || task?.customizationData?.refinementArtwork;
    console.log(customImage);
    if (customImage) {
      // PRÜFUNG: Ist es ein String (URL) oder ein File-Objekt?
      if (typeof customImage === "string") {
        console.log("customImage is a URL, skipping upload.");
        // Es ist bereits eine URL (vom LiveEditor), wir übernehmen sie direkt
        finalTemplate = customImage;
        finalTemplateThumb = customImage; // Thumb ist in diesem Fall das gleiche
      } else {
        // Es ist ein File/Blob (vom Drag & Drop), wir müssen hochladen
        console.log("customImage is a File, uploading...");

        const storage = getStorage();
        const storageRef = ref(
          storage,
          `/templates/${currentMonthYear}/${task.uid}.jpg`
        );
        const uploadResponse = await uploadBytes(storageRef, customImage);
        finalTemplate = await getDownloadURL(uploadResponse.ref);

        // Falls du eine Helper-Funktion getImageUrl hast, nutze sie,
        // ansonsten nimm auch hier die DownloadURL
        if (typeof getImageUrl === "function") {
          finalTemplateThumb = getImageUrl(
            `/templates/${currentMonthYear}/${task.uid}.jpg`
          );
        } else {
          finalTemplateThumb = finalTemplate;
        }
      }
    }

    console.log(task);

    // Creates A DB Entry for task (Excluding personal customer Information)
    await setDoc(doc(db, `tasks`, task?.uid.toString()), {
      taskKey: task.taskKey || "",
      orderKey: task.orderKey || "",
      uid: task.uid,
      orderId: task.orderId,
      referrer: task.referrer || null,
      lineItemId: task.lineItemId,
      orderName: task.orderName,
      orderNote: task.orderNote,
      productType: {
        productName: task.productName,
        ...task.productType,
      },
      printVariant: task.printVariantId || task.printVariant || null,
      printVariantName: task.printVariantName,
      thumb: originalImage || null,
      image: originalImage || null,
      result: task.customizationData.previewImg || null,
      resultThumbnailUrl: task.customizationData.previewImg || null,
      status: taskStatus.CLOSED,
      template: null,
      finalTemplate: finalTemplate,
      finalTemplateThumbnailUrl: finalTemplateThumb,
      editors: {
        IMPORTED: auth.currentUser.uid,
      },
      customizationData: {
        ...task.customizationData,
        refinementArtwork: finalRefineUrl || null,
        // frische Refinement-URL → Zeitstempel für Neueste-Version-Erkennung
        ...(finalRefineUrl ? { refinementArtworkAt: Date.now() } : {}),
        refinementVersions: appendRefinementVersion(
          task.customizationData,
          finalRefineUrl || null,
          Date.now()
        ),
        savedConfig: currentConfig || null,
      },
      createdAt: task.createdAt,
      assignedTo: null, // existentTask means it will not be directed template -> no assignee
      countryCode: task.countryCode,
      shopId: task.shopId,
      numberOfSubTasks: task.numberOfSubTasks,
      originalImage: originalImage || "",
      originalImageThumb: originalImage || "",
      extras: task.extras || [],
      customerEmail: task.customerEmail || "",
    });

    if (sendPreviewCustomer) {
      console.log(
        currentConfig?.orientation ||
          task.customizationData?.configuration?.orientation ||
          null
      );
      console.log(currentConfig?.orientation);
      console.log(task.customizationData?.configuration?.orientation);
      const mailResults = httpsCallable(functions, "mailResults");
      await mailResults({
        taskUid: task.uid,
        template: finalTemplate,
        orientation:
          currentConfig?.orientation ||
          task.customizationData?.configuration?.orientation ||
          null,
        isLivePreview: true,
      });
      // Welche Version der Kunde bekommen hat (Auto-Edit-Grundlage, Mail-Symbol in der Versionsleiste)
      await setDoc(
        doc(db, "tasks", task.uid),
        { customizationData: { previewSent: { at: Date.now(), artworkUrl: currentConfig?.sentArtworkUrl || null, variant: currentConfig?.sentVariant || null, template: finalTemplate || null } } },
        { merge: true }
      );
    }

    try {
      const taskToHide = doc(db, "shopifyTasks", task.uid);
      await deleteDoc(taskToHide);
    } catch (e) {
      console.log("Task was already imported");
    }

    callBack();
  },
};

const mutations = {
  setAllTasks: (state, { tasks, shopId }) =>
    (state.allTasks = {
      ...state.allTasks,
      [shopId]: tasks,
    }),
  addShopifyTask(state, { shopId, task, liveEditor }) {
    if (liveEditor) {
      if (task.productType.isExpress) {
        state.allTasks.live.unshift(task);
      } else {
        state.allTasks.live.push(task);
      }
    } else {
      if (task.productType.isExpress) {
        state.allTasks[shopId].unshift(task);
      } else {
        state.allTasks[shopId].push(task);
      }
    }
  },
  updateShopifyTask(state, { shopId, task, liveEditor }) {
    const id = liveEditor ? "live" : shopId;
    const updatedTasks = state.allTasks[id].map((t) =>
      t.uid === task.uid ? task : t
    );

    state.allTasks = {
      ...state.allTasks,
      [id]: updatedTasks,
    };

    EventBus.$emit(events.TASK_UPDATED, { uid: task.uid });
  },
  deleteShopifyTask(state, { shopId, task }) {
    const updatedTasks = state.allTasks[shopId].find((t) => t.uid !== task.uid);

    state.allTasks = {
      ...state.allTasks,
      [shopId]: updatedTasks,
    };
  },
  setShopifyTasksLoading: (state, bool) => {
    state.initial = bool;
    state.shopifyTasksLoading = bool;
  },
  setSearchTerm: (state, searchTerm) => (state.searchTerm = searchTerm),
  setSelectedShopId: (state, selectedShopId) =>
    (state.selectedShopId = selectedShopId),
  setSearchFilter: (state, filter) => (state.filter = filter),
  setSelectedAssignee: (state, user) => (state.selectedAssignee = user),
};

const state = window.sessionStorage["taskDash"]
  ? JSON.parse(window.sessionStorage["taskDash"]).shopifyTasks
  : Object.assign({}, defaultState);

export default {
  state,
  getters,
  actions,
  mutations,
};
